import type { CalendarEvent } from 'paintscout'
import React from 'react'
import PreviewPopper from './PreviewPopper'
import moment from 'moment'
import { formatName, translateEventType, getTimezone } from '@paintscout/util/builder'
import { escapeHref, removeAuth0Prefix } from '@paintscout/util'
import type { User } from '@paintscout/api'
import { useLocation } from 'react-router-dom'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { useIsMobile } from '../../hooks'
import HtmlContent from '../HtmlContent'

export interface EventPreviewPopperProps {
  event: CalendarEvent
  anchorEl: HTMLElement | null
  setAnchorEl: (el: HTMLElement | null) => void
  users: User[]
  onClose: () => void
  onEditEvent: (event: CalendarEvent) => void
  onCancelEvent: (event: CalendarEvent) => void
  minWidth?: number
}

export default function EventPreviewPopper(props: EventPreviewPopperProps) {
  const { event, anchorEl, setAnchorEl, onClose, onEditEvent, onCancelEvent, users, minWidth } = props
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const mobile = useIsMobile()
  const location = useLocation()
  const onPage = (id: string) => {
    return location.pathname.includes(id)
  }

  return (
    <PreviewPopper
      key={event?.id}
      anchorEl={anchorEl}
      onClose={onClose}
      onConfirm={() => {
        if (anchorEl) {
          setAnchorEl(null)
        }
        onEditEvent(event)
      }}
      onCancel={() => {
        if (anchorEl) {
          setAnchorEl(null)
        }
        onCancelEvent(event)
      }}
      confirmLabel="Edit"
      cancelLabel="Cancel"
      hideFooter={event?.canceled || event?.eventType === '_google-calendar_'}
      cancelButtonDisabled={event?.canceled || moment(event?.start).isBefore(moment(), 'day')}
      minWidth={minWidth}
      details={[
        event?.eventType &&
          mobile && {
            title: 'Event Type',
            value:
              event?.eventType === '_google-calendar_'
                ? (event.title as string)
                : translateEventType({ eventType: event?.eventType, options })
          },
        event?.eventType === '_google-calendar_' && {
          title: 'Imported From',
          value: `Google (${event?.transparency === 'transparent' ? 'Free' : 'Busy'})`
        },
        event?.start &&
          event?.end && {
            title: 'Date',
            value: `${moment(event.start).tz(timezone).format('MMMM D, YYYY, h:mma')} - ${moment(event.end).tz(timezone).format('h:mma')}`,
            canCopy: true
          },
        event?.deal?.name &&
          !onPage(event.deal?.id) && {
            title: 'Deal',
            value: event.deal.name,
            link: () => {
              window.location.href = escapeHref(`/deals/edit/${event.deal?.id}`)
            }
          },
        event?.contact &&
          event.contact.id &&
          !onPage(event.contact.id) && {
            title: 'Contact',
            value: formatName(event.contact),
            link: () => {
              window.location.href = escapeHref(`/people/contact/${event.contact.id}`)
            }
          },
        event?.contact?.companyName &&
          !onPage(event.contact.companyId) && {
            title: 'Company',
            value: event.contact.companyName,
            link: () => {
              window.location.href = escapeHref(`/people/companies/${event.contact.companyId}`)
            }
          },
        event?.address && {
          title: 'Address',
          value: event.address,
          link: () => {
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`,
              '_blank'
            )
          }
        },
        event?.attendees > 0 && {
          title: 'Number of Guests',
          value: event.attendees.toString()
        },
        event?.contact?.email && {
          title: 'Email',
          value: event.contact.email,
          link: () => {
            window.location.href = escapeHref(`mailto:${event.contact.email}`)
          },
          star: event.contact.preferredContactMethod === 'email',
          canCopy: true
        },
        event?.contact?.phoneNumber && {
          title: 'Phone',
          value: event.contact.phoneNumber,
          star: ['phone', 'text'].includes(event.contact.preferredContactMethod),
          link: () => {
            window.location.href = escapeHref(`tel:${event.contact.phoneNumber}`)
          },
          canCopy: true
        },

        event?.estimator && {
          title: 'User',
          value: event.estimator?.name,
          user: users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(event.estimator?.id))
        },
        event?.note &&
          event.note.trim() !== '' &&
          event.note.trim() !== '<p></p>' && {
            title: 'Description',
            // value: striptags(event.note)
            value: <HtmlContent content={event.note} />
          }
      ].filter(Boolean)}
    />
  )
}
